"use client";
import AnimatedText from "./AnimatedText";
import Squircle from "./Squircle";
import translations from "@/lib/translations";

import { useEffect, useRef, useState, forwardRef } from "react";

export default function GiftWheelTrigger({ lang = "fr" }) {
	const dialogRef = useRef(null);
	return (
		<>
			<AnimatedText variant="fade">
				<a onClick={() => dialogRef.current?.showModal()} aria-haspopup="dialog">
					{translations.giftWheel.trigger[lang]}
				</a>
			</AnimatedText>
			<GiftWheelModal ref={dialogRef} lang={lang} />
		</>
	);
}

// Modale accessible + roue perf (JS pur)
const GiftWheelModal = forwardRef(function GiftWheelModal({ lang = "fr" }, ref) {
	const messages = translations.giftWheel.messages[lang];
	const [rotation, setRotation] = useState(-18);
	const [spinning, setSpinning] = useState(false);
	const [result, setResult] = useState(null);
	const [charge, setCharge] = useState(0); // 0..1
	const [messageIndex, setMessageIndex] = useState(0);
	const chargingRef = useRef(false);
	const rafRef = useRef(null);
	const wheelRef = useRef(null);
	const flapperRef = useRef(null);
	const tickRafRef = useRef(null);
	const nextTickRef = useRef(0);
	const prevAngleRef = useRef(null);
	const prevTimeRef = useRef(null);
	const onEndRef = useRef(null);
	const prefReduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

	const targetAngle = 0; // centre du "0" sur l'image (0 = en haut). Ajuste si besoin.

	const TICK_DEG = 18; // taille d'une tranche
	const TICK_OFFSET = -9; // offset car la roue est centrée entre deux taquets

	// Cubic-bezier easing (0.22, 1, 0.36, 1) — légère inertie
	function cubicBezier(p1x, p1y, p2x, p2y) {
		// minimal bezier impl (adapted)
		const cx = 3 * p1x,
			bx = 3 * (p2x - p1x) - cx,
			ax = 1 - cx - bx;
		const cy = 3 * p1y,
			by = 3 * (p2y - p1y) - cy,
			ay = 1 - cy - by;
		const sampleX = (t) => ((ax * t + bx) * t + cx) * t;
		const sampleY = (t) => ((ay * t + by) * t + cy) * t;
		const sampleDerivX = (t) => (3 * ax * t + 2 * bx) * t + cx;
		return function solve(x) {
			// Newton-Raphson
			let t = x;
			for (let i = 0; i < 5; i++) {
				const x2 = sampleX(t) - x;
				const d = sampleDerivX(t);
				if (Math.abs(x2) < 1e-5 || d === 0) break;
				t = t - x2 / d;
			}
			return sampleY(t);
		};
	}
	const easeOut = cubicBezier(0.22, 1, 0.36, 1);

	function triggerBump(ampDeg) {
		const el = flapperRef.current;
		if (!el) return;
		el.style.setProperty("--bump-amp", `-${ampDeg}deg`);
		el.classList.remove("is-bumping");
		// Force reflow to restart animation
		// eslint-disable-next-line no-unused-expressions
		el.offsetWidth;
		el.classList.add("is-bumping");
	}

	function clamp(n, a, b) {
		return Math.max(a, Math.min(b, n));
	}

	function startTickLoop(startAngle, endAngle, duration) {
		if (prefReduced) return; // respect reduced motion
		const startTime = performance.now();
		prevAngleRef.current = startAngle;
		prevTimeRef.current = startTime;

		const step = (now) => {
			const t = clamp((now - startTime) / duration, 0, 1);
			const p = easeOut(t);
			const angle = startAngle + (endAngle - startAngle) * p;

			// Détection de passage de taquet
			const idx = Math.floor((angle - TICK_OFFSET) / TICK_DEG);
			if (idx >= nextTickRef.current) {
				// vitesse angulaire approx
				const prevA = prevAngleRef.current;
				const prevT = prevTimeRef.current;
				const dt = Math.max(1, now - prevT);
				const vel = Math.abs(angle - prevA) / dt; // deg per ms
				const amp = clamp(10 + vel * 400, 10, 30);
				triggerBump(amp);
				nextTickRef.current = idx + 1;
			}

			prevAngleRef.current = angle;
			prevTimeRef.current = now;

			if (t < 1) {
				tickRafRef.current = requestAnimationFrame(step);
			}
		};

		cancelAnimationFrame(tickRafRef.current);
		tickRafRef.current = requestAnimationFrame(step);
	}

	const startCharge = () => {
		if (spinning) return;
		chargingRef.current = true;
		const start = performance.now();
		const duration = 600; // ms to reach full charge
		const tick = (t) => {
			if (!chargingRef.current) return;
			const p = Math.min(1, (t - start) / duration);
			setCharge(p);
			rafRef.current = requestAnimationFrame(tick);
		};
		cancelAnimationFrame(rafRef.current);
		rafRef.current = requestAnimationFrame(tick);
	};

	const stopCharge = () => {
		if (!chargingRef.current) return;
		chargingRef.current = false;
		cancelAnimationFrame(rafRef.current);
		const power = charge; // snapshot
		setCharge(0);
		spin(power);
	};

	const spin = (power = 0) => {
		if (spinning) return;
		setResult(null);
		setSpinning(true);

		const fullSpins = 1 + Math.round(3 * power); // 1 à 8 tours selon la charge
		const need = (targetAngle - (rotation % 360) + 360) % 360; // delta pour arriver au "0" de l'image
		const delta = fullSpins * 360 + need;
		const newRotation = rotation + delta; // nouvelle rotation finale

		const base = 6000; // temps minimum que met la roue pour tourner quand tu charges au minimum
		const duration = prefReduced ? 0.01 : base;

		const el = wheelRef.current;
		if (!el) return;

		el.style.setProperty("--spin-duration", `${duration}ms`);
		el.classList.add("is-spinning");
		requestAnimationFrame(() => setRotation(newRotation));

		nextTickRef.current = Math.floor((rotation - TICK_OFFSET) / TICK_DEG) + 1;
		startTickLoop(rotation, newRotation, duration);

		const onEnd = () => {
			el.classList.remove("is-spinning");
			setSpinning(false);
			setMessageIndex((prev) => (prev + 1) % messages.length);
			setResult(translations.giftWheel.result[lang]); // toujours perdant 😅
			cancelAnimationFrame(tickRafRef.current);
			const flap = flapperRef.current;
			if (flap) flap.classList.remove("is-bumping");
			el.removeEventListener("transitionend", onEnd);
			onEndRef.current = null;
		};
		onEndRef.current = onEnd;
		el.addEventListener("transitionend", onEndRef.current);
	};

	const resetGame = () => {
		// cancel any running animations
		cancelAnimationFrame(rafRef.current);
		cancelAnimationFrame(tickRafRef.current);
		// remove bumping class
		const flap = flapperRef.current;
		if (flap) flap.classList.remove("is-bumping");
		// remove spinning class and transition listener
		const wheel = wheelRef.current;
		if (wheel) {
			wheel.classList.remove("is-spinning");
			if (onEndRef.current) {
				wheel.removeEventListener("transitionend", onEndRef.current);
				onEndRef.current = null;
			}
		}
		// reset states
		setRotation(-18);
		setSpinning(false);
		setResult(null);
		setCharge(0);
		// reset tick index
		nextTickRef.current = 0;
		prevAngleRef.current = null;
		prevTimeRef.current = null;
	};

	const requestClose = () => {
		const dialog = ref?.current;
		if (!dialog) return;
		// add closing state for CSS animation
		dialog.setAttribute("data-closing", "");
		const handle = () => {
			dialog.removeAttribute("data-closing");
			dialog.close();
			resetGame();
			dialog.removeEventListener("animationend", handle);
		};
		dialog.addEventListener("animationend", handle);
	};

	// Fermer au clic backdrop
	useEffect(() => {
		const dialog = ref?.current;
		if (!dialog) return;
		const handleClick = (e) => {
			const rect = dialog.getBoundingClientRect();
			const inDialog = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
			if (!inDialog) requestClose();
		};
		dialog.addEventListener("click", handleClick);
		return () => dialog.removeEventListener("click", handleClick);
	}, [ref]);

	useEffect(() => {
		const dialog = ref?.current;
		if (!dialog) return;
		const onCancel = (e) => {
			e.preventDefault(); // prevent instant close
			requestClose();
		};
		dialog.addEventListener("cancel", onCancel);
		return () => dialog.removeEventListener("cancel", onCancel);
	}, [ref]);

	useEffect(() => {
		const dialog = ref?.current;
		if (!dialog) return;
		const onClose = () => {
			resetGame();
		};
		dialog.addEventListener("close", onClose);
		return () => dialog.removeEventListener("close", onClose);
	}, [ref]);

	useEffect(() => {
		return () => {
			cancelAnimationFrame(rafRef.current);
			cancelAnimationFrame(tickRafRef.current);
		};
	}, []);

	return (
		<dialog ref={ref} role="dialog" aria-labelledby="giftwheel-title" className="modal-wheel" data-lenis-prevent data-lenis-prevent-wheel data-lenis-prevent-touch>
			<div className="modal-container" data-lenis-prevent data-lenis-prevent-wheel data-lenis-prevent-touch>
				<h3 id="giftwheel-title">{translations.giftWheel.title[lang]}</h3>
				<p>{translations.giftWheel.description[lang]}</p>
				<button onClick={requestClose} aria-label={translations.giftWheel.close[lang]} className="close">
					✕
				</button>
				<div aria-hidden className="wheel">
					<div ref={flapperRef} className="flapper" aria-hidden="true"></div>
					<img ref={wheelRef} className="wheel-img" src="/images/gift-wheel.png" alt="" decoding="async" loading="lazy" style={{ transform: `rotate(${rotation}deg)` }} />
					<div aria-live="polite">
						{result && (
							<>
								<div className="result">
									<h4>{translations.giftWheel.lose[lang]}</h4> {messages[messageIndex]}
								</div>
							</>
						)}
					</div>
				</div>
				<div className="separator-s"></div>
				<div className="actions">
					<div className="power-bar-container">
						<div className="power-bar">
							<div className="power-bar-fill" style={{ width: `${Math.round(charge * 100)}%` }}></div>
						</div>
						<p>{translations.giftWheel.power[lang]}</p>
					</div>
					<div className="button">
						<div className="button-container">
							<Squircle size="button"></Squircle>
							<button
								type="button"
								className="btn"
								disabled={spinning}
								onPointerDown={startCharge}
								onPointerUp={stopCharge}
								onPointerLeave={stopCharge}
								onKeyDown={(e) => {
									if (e.key === " " || e.key === "Enter") {
										e.preventDefault();
										startCharge();
									}
								}}
								onKeyUp={(e) => {
									if (e.key === " " || e.key === "Enter") {
										e.preventDefault();
										stopCharge();
									}
								}}
								aria-label={spinning ? translations.giftWheel.ariaLabels.wheelSpinning[lang] : charge > 0 ? `${translations.giftWheel.ariaLabels.chargePercent[lang]} ${Math.round(charge * 100)}%` : translations.giftWheel.ariaLabels.chargePower[lang]}
							>
								{spinning ? translations.giftWheel.spinning[lang] : translations.giftWheel.spinButton[lang]}
							</button>
						</div>
					</div>
				</div>
			</div>
		</dialog>
	);
});
